import { useEffect, useState } from 'react';

import { textureCandidates, textureUrl } from '../lib/itemTexture.ts';

/**
 * Minecraft item texture, from the pinned public asset mirror.
 *
 * There is no manifest to consult in the browser, so the resolver hands us an
 * ordered list of paths and each `onError` advances to the next one. A material
 * that matches its own texture - most of them - loads on the first try; the
 * awkward ones (a slab that is drawn from planks, a spawn egg that shares one
 * tinted texture, a block whose flat name only exists as `_side`) walk a few
 * steps down the chain.
 *
 * When the chain runs out we draw initials rather than borrowing a plausible
 * texture from another item. Chests, heads and shields exist only as entity
 * models, and showing a chest as a plank is a wrong picture, not a fallback.
 */
export function ItemIcon({
  material,
  size = 32,
  alt = '',
}: {
  material: string;
  size?: number;
  alt?: string;
}) {
  const candidates = textureCandidates(material);
  const [index, setIndex] = useState(0);

  // A recycled component instance (a virtualised table row, a re-sorted list)
  // would otherwise keep the previous item's position in the chain and start
  // several failed requests deep.
  useEffect(() => setIndex(0), [material]);

  const path = candidates[index];

  if (path == null) {
    return (
      <span
        className="item-icon item-icon-fallback"
        style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }}
        aria-hidden={alt === '' ? true : undefined}
        title={alt || material}
      >
        {material.slice(0, 2).toUpperCase()}
      </span>
    );
  }

  return (
    <img
      className="item-icon"
      style={{ width: size, height: size }}
      src={textureUrl(path)}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => setIndex((current) => current + 1)}
    />
  );
}
